"use client";

import NextLink from "next/link";
import { useSearchParams } from "next/navigation";

import { ChevronLeftIcon } from "@/components/icons/chevronLeft.icon";
import { ForwardSlash } from "@/components/icons/forwardSlash.icon";
import { Link } from "@/components/link/link.component";
import { Text } from "@/components/text/text.component";

import { appendQueryString, preservePageSearchParams } from "@/utils/url.util";

import { CONTENT_MODAL_QUERY_PARAM } from "@/constants/contentModal.constants";
import { urlFor } from "@/sanity/utils";

import { RichText } from "../richText/richText.component";
import type { HeroSectionProps } from "./heroSection.component";

const SPLIT_PATTERN_SRC = "/assets/patterns/hero-split-pattern.png";
const SPLIT_HERO_SUPERSCRIPT =
  "[&_sup]:align-baseline! [&_sup]:text-[0.65em]! [&_sup]:leading-none!";

export const SplitHeroSection = ({ block, pageName }: HeroSectionProps) => {
  const desktopImageUrl = block?.backgroundImage
    ? urlFor(block.backgroundImage).width(1440).fit("max").url()
    : null;

  const mobileImageUrl = block?.mobileBackgroundImage
    ? urlFor(block.mobileBackgroundImage)
        .width(800)
        .height(1000)
        .fit("max")
        .url()
    : desktopImageUrl;

  const ctaHref =
    block?.ctaLink?.url != null
      ? appendQueryString(block.ctaLink.url, block.ctaLink.queryString)
      : null;

  return (
    <section
      data-testid="hero-section"
      className="relative flex w-full flex-col lg:min-h-[600px]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 hidden lg:flex">
        <div className="bg-secondary-300 relative w-1/2">
          <img
            src={SPLIT_PATTERN_SRC}
            alt=""
            aria-hidden
            data-testid="hero-section-pattern"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        {desktopImageUrl ? (
          <div
            className="w-1/2 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${desktopImageUrl})` }}
          />
        ) : (
          <div className="w-1/2" />
        )}
      </div>

      {desktopImageUrl ? (
        <div className="relative h-[265px] w-full lg:hidden">
          <div
            aria-hidden
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${mobileImageUrl ?? desktopImageUrl})`,
            }}
          />
        </div>
      ) : null}

      <div className="relative z-10 mx-auto flex w-full max-w-360 flex-col lg:min-h-[600px] lg:flex-row">
        <div className="bg-secondary-300 relative flex flex-col justify-center px-5 py-8 text-white lg:w-1/2 lg:justify-start lg:bg-transparent lg:px-12 lg:pt-36 lg:pb-10">
          <img
            src={SPLIT_PATTERN_SRC}
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-0 h-full w-full object-cover lg:hidden"
          />
          <div className="relative z-10 flex w-full max-w-[34rem] flex-1 flex-col gap-4 lg:gap-8">
            {pageName ? <SplitHeroBreadcrumb name={pageName} /> : null}
            <div className="flex flex-1 flex-col justify-center gap-4 lg:gap-8">
              <div className="flex flex-col gap-2">
                <div
                  className={`font-brown-std text-primary [&_*]:text-primary! text-[20px] leading-[1.15] font-bold lg:text-[40px] [&_*]:font-bold! [&_*:not(sup)]:text-[20px]! [&_*:not(sup)]:leading-[1.15]! lg:[&_*:not(sup)]:text-[40px]! ${SPLIT_HERO_SUPERSCRIPT}`}
                  data-testid="hero-section-title">
                  <RichText>{block?.title}</RichText>
                </div>

                {block?.subtitle ? (
                  <div
                    className={`font-brown-std text-[16px] leading-[1.15] font-bold text-white lg:text-[32px] [&_*]:font-bold! [&_*]:text-white! [&_*:not(sup)]:text-[16px]! [&_*:not(sup)]:leading-[1.15]! lg:[&_*:not(sup)]:text-[32px]! ${SPLIT_HERO_SUPERSCRIPT}`}
                    data-testid="hero-section-subtitle">
                    <RichText>{block.subtitle}</RichText>
                  </div>
                ) : null}

                {block?.body ? (
                  <div
                    className={`max-w-xl text-[14px] leading-[1.3] font-normal text-white lg:text-[16px] lg:leading-6 [&_*]:font-normal! [&_*]:text-white! [&_*:not(sup)]:text-[14px]! [&_*:not(sup)]:leading-[1.3]! lg:[&_*:not(sup)]:text-[16px]! lg:[&_*:not(sup)]:leading-6! ${SPLIT_HERO_SUPERSCRIPT}`}
                    data-testid="hero-section-body">
                    <RichText>{block.body}</RichText>
                  </div>
                ) : null}
              </div>

              <div className="flex flex-col gap-2">
                {block?.ctaLabel && ctaHref ? (
                  <SplitHeroCta
                    href={ctaHref}
                    label={block.ctaLabel}
                    openInNewTab={block.ctaLink?.openInNewTab}
                  />
                ) : null}

                {block?.legalCallout ? (
                  <div
                    data-testid="hero-section-legal-callout"
                    className={`max-w-xl text-[12px] leading-[1.3] text-white [&_*]:text-white! [&_*:not(sup)]:text-[12px]! [&_*:not(sup)]:leading-[1.3]! [&_a]:underline ${SPLIT_HERO_SUPERSCRIPT}`}>
                    <RichText interactiveQueryParam={CONTENT_MODAL_QUERY_PARAM}>
                      {block.legalCallout}
                    </RichText>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const SplitHeroCta = ({
  href,
  label,
  openInNewTab,
}: {
  href: string;
  label: string;
  openInNewTab?: boolean | null;
}) => {
  const searchParams = useSearchParams();
  const hrefWithContext = preservePageSearchParams(href, searchParams, [
    CONTENT_MODAL_QUERY_PARAM,
  ]);

  return (
    <Link
      data-testid="hero-section-cta"
      primary
      href={hrefWithContext}
      className="mt-0 w-fit min-w-40 px-6 py-2 text-[14px] uppercase lg:mt-2 lg:px-10 lg:py-3 lg:text-[18px]"
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? "noopener noreferrer" : undefined}>
      {label}
    </Link>
  );
};

const SplitHeroBreadcrumb = ({ name }: { name: string }) => (
  <nav
    data-testid="hero-section-breadcrumb"
    aria-label="Breadcrumb"
    className="text-white lg:mb-0">
    <NextLink
      href="/"
      className="flex items-center gap-0.5 pt-2 lg:hidden"
      aria-label={`Back to Home, ${name}`}>
      <ChevronLeftIcon
        width={24}
        height={24}
        className="shrink-0"
        aria-hidden
      />
      <Text variant="body6" component="span">
        {name}
      </Text>
    </NextLink>
    <ol className="hidden items-center gap-2 lg:flex">
      <li>
        <NextLink href="/" className="underline underline-offset-2">
          <Text variant="body6" component="span" className="text-white">
            Home
          </Text>
        </NextLink>
      </li>
      <li className="flex items-center gap-2">
        <ForwardSlash width={16} height={16} aria-hidden />
        <Text
          variant="body6"
          component="span"
          className="text-white"
          aria-current="page">
          {name}
        </Text>
      </li>
    </ol>
  </nav>
);
